import { GetOptions, Payment, QueryConstraint } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { getNextPageParam } from "../utils/page-params";
import { fetchAllPages } from "../utils/fetch-all-pages";

const databaseService = serviceHost.getDatabaseService();
const paymentRepository = repositoryHost.getPaymentRepository(databaseService);
const functionsService = serviceHost.getFunctionsService();

async function getPaymentsByTarget(payload: {
  appliesToType: "po" | "invoice";
  appliesToId: string;
}): Promise<Payment[]> {
  const { appliesToType, appliesToId } = payload;

  const payments = await paymentRepository.getAll({
    queryConstraints: [
      {
        field: "appliesToType",
        operator: "==",
        value: appliesToType,
      },
      {
        field: "appliesToId",
        operator: "==",
        value: appliesToId,
      },
    ],
    orderBy: {
      field: "createdAt",
      direction: "desc",
    },
    pagination: {
      limit: 100,
    },
  });

  return payments.sort((left, right) => {
    const leftTimestamp = new Date(left.paidAt || left.createdAt).getTime();
    const rightTimestamp = new Date(right.paidAt || right.createdAt).getTime();
    return rightTimestamp - leftTimestamp;
  });
}

export const usePayments = (options: GetOptions<Payment>) => {
  return useInfiniteQuery({
    queryKey: ["payments", "get", JSON.stringify(options)],
    queryFn: ({ pageParam }) =>
      paymentRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

/** Auto-pages every payment matching `queryConstraints`, newest first. */
export const fetchAllPayments = (
  queryConstraints: QueryConstraint<keyof Payment & string>[] = [],
): Promise<Payment[]> =>
  fetchAllPages<Payment>((options) => paymentRepository.getAll(options), {
    queryConstraints,
    orderBy: { field: "createdAt", direction: "desc" },
  });

/**
 * Loads ALL payments by auto-paging.
 *
 * Finance derives per-invoice and per-PO cleared totals from this, so a capped
 * read would silently under-report what has been paid once the collection
 * outgrows the cap.
 */
export const useAllPayments = () => {
  const query = useQuery({
    queryKey: ["payments", "all"],
    queryFn: () => fetchAllPayments(),
  });

  return {
    payments: useMemo(() => query.data ?? [], [query.data]),
    isLoading: query.isLoading,
  };
};

export const usePaymentsByTarget = (payload: {
  appliesToType: "po" | "invoice";
  appliesToId: string;
}) => {
  const { appliesToType, appliesToId } = payload;

  return useQuery({
    queryKey: ["payments", "by-target", appliesToType, appliesToId],
    enabled: appliesToId.length > 0,
    queryFn: () =>
      getPaymentsByTarget({
        appliesToType,
        appliesToId,
      }),
  });
};

export const usePaymentsByTargets = (payload: {
  appliesToType: "po" | "invoice";
  appliesToIds: string[];
}) => {
  const { appliesToType, appliesToIds } = payload;
  const uniqueAppliesToIds = Array.from(new Set(appliesToIds.filter(Boolean)));

  const queries = useQueries({
    queries: uniqueAppliesToIds.map((appliesToId) => ({
      queryKey: ["payments", "by-target", appliesToType, appliesToId],
      queryFn: () =>
        getPaymentsByTarget({
          appliesToType,
          appliesToId,
        }),
      enabled: appliesToId.length > 0,
    })),
  });

  const paymentMap = new Map<string, Payment>();

  for (const query of queries) {
    for (const payment of query.data || []) {
      paymentMap.set(payment.id, payment);
    }
  }

  const data = Array.from(paymentMap.values()).sort((left, right) => {
    const leftTimestamp = new Date(left.paidAt || left.createdAt).getTime();
    const rightTimestamp = new Date(right.paidAt || right.createdAt).getTime();
    return rightTimestamp - leftTimestamp;
  });

  return {
    data,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    isError: queries.some((query) => query.isError),
  };
};

export const useUpdatePaymentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Routed through the updatePaymentStatus callable (not a direct repository
    // write) so the linked PO/invoice finance status — paymentStatus + amountPaid
    // — is recalculated; a direct write would leave the paid total stale.
    mutationFn: (payload: {
      paymentId: string;
      status: "pending" | "cleared" | "failed" | "reversed" | "rejected";
    }) => functionsService.updatePaymentStatus(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
    },
  });
};
